import { useState, useEffect, useMemo, createContext, useRef } from 'react';
import { Client } from 'boardgame.io/react';
import { SocketIO } from 'boardgame.io/multiplayer';
import { LobbyClient } from 'boardgame.io/client';
import { GrandSnatchGame } from './game/Game';
import { GrandSnatchBoard } from './components/Board';
import { Login } from './components/Login';
import { Collection } from './components/Collection';
import { RunScreen, RunStarter, type UserRunRecord } from './components/RunScreen';
import { DialogProvider, useDialog } from './components/Dialog';
import { ErrorBoundary } from './components/ErrorBoundary';
import { VerifyEmailPage } from './components/VerifyEmailPage';
import { ResetPasswordPage } from './components/ResetPasswordPage';
import { LegalPage, type LegalDoc } from './components/LegalPage';
import { AccountSettings } from './components/AccountSettings';
import { AdminDashboard } from './components/AdminDashboard';
import { RegistrationGate } from './components/RegistrationGate';
import { useUiScale } from './components/useUiScale';
import { FeedbackWidget } from './components/FeedbackWidget';
import type { CardData, GrandSnatchState } from './game/types';
import { SERVER_ORIGIN } from './config';
import { initTelemetry, trackEvent } from './utils/telemetry';
import { validateDeckComposition } from './game/deck';
import './index.css';

const serverOrigin = SERVER_ORIGIN;

export const BotContext = createContext<boolean>(false);

// Provided by App when a guest tutorial match is active. Board.tsx reads this
// to know whether to render the TutorialOverlay.
export const TutorialContext = createContext<{ active: boolean }>({ active: false });

// Context propagato a Board.tsx quando il match parte da dentro una run:
// - deckUsedKey: 'A' | 'B'. Usato solo nell'onMatchEnd per identificare al
//   server quale dei 2 mazzi della run è stato impiegato (per snatch lossage,
//   forced exit accounting). NON serve più a far passare le carte: dopo
//   F7 v2 il server pre-popola G.players[seat].selectedCards al setup del
//   match leggendole direttamente dal `user_runs.decks_snapshot[deckKey]`.
// - onMatchEnd: callback invocato esattamente UNA volta quando ctx.gameover
//   diventa truthy. Riceve matchID dal Board (closure-safe — vedi commento
//   in RunMatchCtx) per chiamare /auth/runs/current/record-match.
export interface MatchGameoverData {
  winner?: string;
  score0?: number;
  score1?: number;
  [key: string]: any;
}
export interface RunMatchCtx {
  deckUsedKey: 'A' | 'B' | null;
  // Board passes its own matchID (from bgio props) so the callback gets it
  // unconditionally, even if App's `matchID` state was still null at the
  // time `setRunMatchCtx` snapshotted the callback closure. Without this,
  // record-match POSTs silently bail with a stale matchID = null.
  onMatchEnd: ((G: GrandSnatchState, playerID: string, gameover: MatchGameoverData, matchID: string) => void) | null;
}
export const RunMatchContext = createContext<RunMatchCtx>({
  deckUsedKey: null,
  onMatchEnd: null,
});

const GameClient = Client({
  game: GrandSnatchGame,
  board: GrandSnatchBoard,
  multiplayer: SocketIO({ server: serverOrigin }),
});

const lobbyClient = new LobbyClient({ server: serverOrigin });

// Helper: build the Authorization header `init` accepted by every LobbyClient
// method. Inlined at each call site (LobbyClient is module-scoped, token is
// component-scoped, no clean way to inject globally). The lobby access-
// control middleware on the server refuses any call without this header.
function bearerInit(token: string): RequestInit {
  return { headers: { Authorization: `Bearer ${token}` } };
}

interface AuthUser {
  id: string;
  username: string;
  avatarId: number | null;
  email: string | null;
  emailVerified: boolean;
  acceptedTermsVersion?: string | null;
  isAdmin?: boolean;
  // Lifetime stash of resources earned via successful extracts (suit → count).
  // Optional in the type so legacy localStorage entries don't fail to hydrate.
  bankedResources?: Record<string, number>;
  stats?: {
    matchesPlayed: number;
    matchesWon: number;
    matchesLost: number;
    matchesDrawn: number;
  };
  settings?: {
    runTutorialSeen?: boolean;
  };
}
interface AuthState { token: string; user: AuthUser }

const AUTH_STORAGE_KEY = 'gs_auth';

function loadAuth(): AuthState | null {
  try {
    const raw = localStorage.getItem(AUTH_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<AuthState>;
    if (!parsed?.token || !parsed.user?.id) return null;
    // Backfill optional fields for users whose localStorage was written before
    // the email-verification refactor — missing email → null (no banner),
    // missing flag → treat as unverified (the /auth/me refresh on banner mount
    // will set the real value if the address is, in fact, verified).
    return {
      token: parsed.token,
      user: {
        id: parsed.user.id,
        username: parsed.user.username ?? '',
        avatarId: parsed.user.avatarId ?? null,
        email: parsed.user.email ?? null,
        emailVerified: Boolean(parsed.user.emailVerified),
        isAdmin: Boolean(parsed.user.isAdmin),
        bankedResources: parsed.user.bankedResources ?? {},
        acceptedTermsVersion: parsed.user.acceptedTermsVersion,
        stats: parsed.user.stats,
        settings: parsed.user.settings ?? { runTutorialSeen: false },
      },
    };
  } catch { return null; }
}

type Tab = 'lobby' | 'collection' | 'run' | 'account' | 'admin';
type ServerStatus = 'unknown' | 'ok' | 'down';
// Mirrors the server's BotDifficulty (src/server/botStrategy.ts) — kept as a
// local union so the client doesn't import a server-side module.
type BotDifficulty = 'easy' | 'medium' | 'hard';

// Wrapper esterno: monta il DialogProvider così tutti i child possono usare useDialog().
// Prima del DialogProvider, intercetta i due path SPA dedicati ai link email
// (/verify e /reset). Sono pagine standalone: niente bisogno di auth, niente
// bisogno di socket. Caddy/Vite servono sempre index.html (SPA fallback) e
// noi qui smistiamo in base a window.location.pathname.
export default function App() {
  const path = typeof window !== 'undefined' ? window.location.pathname : '/';
  if (path === '/verify') return <VerifyEmailPage />;
  if (path === '/reset')  return <ResetPasswordPage />;
  const legalMatch = path.match(/^\/legal\/(privacy|terms|cookies)$/);
  if (legalMatch) return <LegalPage doc={legalMatch[1] as LegalDoc} />;
  return (
    <DialogProvider>
      <AppInner />
    </DialogProvider>
  );
}

function AppInner() {
  const dialog = useDialog();
  // Scala della board per schermi sotto 1920×1080 (vedi useUiScale.ts).
  useUiScale();
  const [auth, setAuth] = useState<AuthState | null>(() => loadAuth());
  const [tab, setTab] = useState<Tab>('lobby');
  const [hasNewLoot, setHasNewLoot] = useState<boolean>(false);

  useEffect(() => {
    initTelemetry();
    const checkLoot = () => {
      const lootStr = localStorage.getItem('gs_new_loot');
      if (lootStr) {
        try {
          const loot = JSON.parse(lootStr);
          setHasNewLoot(!!loot.unseen);
        } catch {
          setHasNewLoot(false);
        }
      } else {
        setHasNewLoot(false);
      }
    };
    checkLoot();
    window.addEventListener('gs-new-loot-updated', checkLoot);
    window.addEventListener('storage', checkLoot);
    return () => {
      window.removeEventListener('gs-new-loot-updated', checkLoot);
      window.removeEventListener('storage', checkLoot);
    };
  }, []);



  // In-match state
  const [player, setPlayer] = useState<string | null>(null);
  const [matchID, setMatchID] = useState<string | null>(null);
  const [credentials, setCredentials] = useState<string | null>(null);
  const [isBot, setIsBot] = useState<boolean>(false);
  const [matches, setMatches] = useState<any[]>([]);
  const [serverStatus, setServerStatus] = useState<ServerStatus>('unknown');

  // Run state
  const [currentRun, setCurrentRun] = useState<UserRunRecord | null>(null);
  const [runStarterOpen, setRunStarterOpen] = useState(false);
  // True while the "choose AI difficulty" modal is open (friendly vs-bot flow).
  const [botPickerOpen, setBotPickerOpen] = useState(false);
  const [runMatchCtx, setRunMatchCtx] = useState<RunMatchCtx>({
    deckUsedKey: null,
    onMatchEnd: null,
  });

  // State for pre-match deck selection in friendly modes
  const [deckSelectorMode, setDeckSelectorMode] = useState<'pvp' | 'bot' | null>(null);
  const [selectedDeckIdForAI, setSelectedDeckIdForAI] = useState<string | null | undefined>(undefined);

  // Guest / tutorial state. The guest token is held in React state only —
  // never persisted to localStorage (so Board's readAuthToken returns null,
  // triggering the suit-picker path — exactly what we want for guests).
  const [isGuest, setIsGuest] = useState(false);
  const [guestMatchOver, setGuestMatchOver] = useState(false);
  const [guestToken, setGuestToken] = useState<string | null>(null);
  const [tutorialFinalState, setTutorialFinalState] = useState<GrandSnatchState | null>(null);
  const isGuestRef = useRef(false);
  useEffect(() => { isGuestRef.current = isGuest; }, [isGuest]);

  const runMatchCtxRef = useRef(runMatchCtx);
  useEffect(() => { runMatchCtxRef.current = runMatchCtx; }, [runMatchCtx]);

  useEffect(() => {
    if (guestMatchOver) {
      trackEvent('funnel_register_prompt');
    }
  }, [guestMatchOver]);

  // Carica la run attiva al login + dopo logout.
  const authUserId = auth?.user?.id;
  const authToken = auth?.token;
  useEffect(() => {
    if (!authUserId || !authToken) { setCurrentRun(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${SERVER_ORIGIN}/auth/runs/current`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        if (cancelled) return;
        if (res.ok) {
          const j = await res.json();
          setCurrentRun(prev => {
            if (!j.run && prev && (prev.status === 'failed' || prev.status === 'extracted')) {
              return prev;
            }
            return j.run ?? null;
          });
          if (j.run && j.run.status === 'active') setTab('run');
        }
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [authUserId, authToken]);

  const handleAuth = (token: string, user: AuthUser) => {
    const normalized: AuthUser = {
      id: user.id,
      username: user.username,
      avatarId: user.avatarId ?? null,
      email: user.email ?? null,
      emailVerified: Boolean(user.emailVerified),
      isAdmin: Boolean(user.isAdmin),
      bankedResources: user.bankedResources ?? {},
      acceptedTermsVersion: user.acceptedTermsVersion ?? null,
      stats: user.stats,
      settings: user.settings ?? { runTutorialSeen: false },
    };
    const next = { token, user: normalized };
    localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(next));
    setAuth(next);
  };

  // Patch the local auth state (e.g. after the user picks a new avatar).
  // Persists so the next reload reflects the change without hitting /auth/me.
  const updateAuthUser = (patch: Partial<AuthUser>) => {
    setAuth(prev => {
      if (!prev) return prev;
      const next = { ...prev, user: { ...prev.user, ...patch } };
      localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  };

  const logout = () => {
    localStorage.removeItem(AUTH_STORAGE_KEY);
    setAuth(null);
    setPlayer(null);
    setMatchID(null);
    setCredentials(null);
    setIsBot(false);
  };

  const refreshUserProfile = async () => {
    if (!auth) return;
    try {
      const res = await fetch(`${SERVER_ORIGIN}/auth/me`, {
        headers: { Authorization: `Bearer ${auth.token}` },
      });
      if (res.ok) {
        const json = await res.json();
        const userObj = json.user || (json.id ? json : null);
        if (userObj) {
          updateAuthUser(userObj);
        }
      }
    } catch (e) {
      console.error('Failed to refresh user profile:', e);
    }
  };

  useEffect(() => {
    if (tab === 'collection') {
      void refreshUserProfile();
      const lootStr = localStorage.getItem('gs_new_loot');
      if (lootStr) {
        try {
          const loot = JSON.parse(lootStr);
          if (loot.unseen) {
            loot.unseen = false;
            localStorage.setItem('gs_new_loot', JSON.stringify(loot));
            setHasNewLoot(false);
            window.dispatchEvent(new CustomEvent('gs-new-loot-updated'));
          }
        } catch (e) {
          // ignore
        }
      }
    }
  }, [tab]);

  // -------------------- Guest / tutorial handlers --------------------

  // Requests a short-lived guest JWT, then creates + joins a tutorial match vs
  // the easy bot. No gs_auth written to localStorage: Board falls back to the
  // suit-picker UI automatically (readAuthToken returns null for guests).
  const playAsGuest = async () => {
    try {
      const sessionRes = await fetch(`${SERVER_ORIGIN}/auth/guest-session`, { method: 'POST' });
      if (!sessionRes.ok) {
        void dialog.alert({ title: 'Error', message: 'Could not start guest session. Try again.', variant: 'error' });
        return;
      }
      const { token } = await sessionRes.json() as { token: string };
      setGuestToken(token);
      setTutorialFinalState(null);
      setRunMatchCtx({
        deckUsedKey: null,
        onMatchEnd: (G, _playerID, _gameover, _matchIDFromBoard) => {
          setTutorialFinalState(G);
          trackEvent('funnel_tutorial_complete');
        },
      });
      const createRes = await lobbyClient.createMatch('grand-snatch', {
        numPlayers: 2,
        setupData: { botSeat: '1', botDifficulty: 'easy', tutorial: true },
      }, bearerInit(token));
      const joinRes = await lobbyClient.joinMatch('grand-snatch', createRes.matchID, {
        playerID: '0',
        playerName: 'Guest',
      }, bearerInit(token));
      setMatchID(createRes.matchID);
      setCredentials(joinRes.playerCredentials ?? null);
      setPlayer('0');
      setIsBot(false);
      setIsGuest(true);
      trackEvent('funnel_tutorial_start');
    } catch (e) {
      console.error('playAsGuest error:', e);
      void dialog.alert({
        title: 'Connection error',
        message: "Couldn't start the tutorial match. Check that the server is running.",
        variant: 'error',
      });
      setGuestToken(null);
    }
  };

  // Called after guest registers/logs in from RegistrationGate: store auth,
  // clear the guest match, and land in the lobby as a real user.
  const handleGuestAuth = (token: string, user: AuthUser) => {
    handleAuth(token, user);
    setIsGuest(false);
    setGuestToken(null);
    setGuestMatchOver(false);
    setPlayer(null);
    setMatchID(null);
    setCredentials(null);
    setIsBot(false);
    setTutorialFinalState(null);
  };

  // Called when the guest dismisses the RegistrationGate without registering.
  // Tear down everything and show Login.
  const handleGuestMaybeLater = () => {
    setIsGuest(false);
    setGuestToken(null);
    setGuestMatchOver(false);
    setPlayer(null);
    setMatchID(null);
    setCredentials(null);
    setIsBot(false);
    setTutorialFinalState(null);
  };

  // Il bottino del tutorial lo ricalcola il server dallo stato del match:
  // qui passiamo solo quale match e con quale sessione guest è stato giocato.
  const tutorialClaim = tutorialFinalState && matchID && guestToken
    ? { tutorialMatchID: matchID, guestToken }
    : undefined;

  const updateUserSettings = async (settings: { runTutorialSeen?: boolean }) => {
    if (!auth) return;
    try {
      const res = await fetch(`${SERVER_ORIGIN}/auth/settings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${auth.token}`,
        },
        body: JSON.stringify(settings),
      });
      if (res.ok) {
        const updatedUser = await res.json() as AuthUser;
        updateAuthUser({ settings: updatedUser.settings });
      }
    } catch (e) {
      console.error('Failed to update settings:', e);
    }
  };

  const refreshRun = async () => {
    if (!auth) return;
    try {
      const res = await fetch(`${SERVER_ORIGIN}/auth/runs/current`, {
        headers: { Authorization: `Bearer ${auth.token}` },
      });
      if (res.ok) {
        const j = await res.json();
        // Il server torna solo run con status='active'. Se localmente abbiamo
        // una run in stato terminale (failed/extracted), la preserviamo finché
        // l'utente non la dismette esplicitamente — così la schermata di
        // recap in RunScreen ha il tempo di essere mostrata. Senza questa
        // preservazione, dopo una forced exit l'utente finisce in un limbo
        // vuoto (tab='run' ma currentRun=null).
        setCurrentRun(prev => {
          if (!j.run && prev && (prev.status === 'failed' || prev.status === 'extracted')) {
            return prev;
          }
          return j.run ?? null;
        });
      }
    } catch { /* silent */ }
  };

  // Fine run riuscita (Safehouse o boss): segna le carte nuove per il badge
  // della collezione e aggiorna le risorse banked dell'utente.
  const bankRunLoot = (gained: Record<string, number>, banked: Record<string, number>, gainedCardIds: string[]) => {
    const loot = { cardIds: gainedCardIds || [], resources: gained || {}, unseen: true };
    localStorage.setItem('gs_new_loot', JSON.stringify(loot));
    window.dispatchEvent(new CustomEvent('gs-new-loot-updated'));
    updateAuthUser({ bankedResources: banked });
  };

  const abandonOrFailRun = () => {
    setCurrentRun(null);
    setTab('lobby');
  };

  // Quando il giocatore clicca un nodo regular/boss adiacente in RunScreen e
  // sceglie un mazzo (A o B), prepara il context per il prossimo match e crea
  // il match vs bot riusando lo stesso flusso di playVsBot. Memorizza anche
  // il targetNodeId: se vince, handleMatchEnd farà advance-to-node.
  const startMatchInRun = async (deckKey: 'A' | 'B', deckCards: CardData[], targetNodeId: string) => {
    if (!auth || !currentRun) return;
    // F7 v2: `deckCards` non viaggia più nel move payload — il server le pesca
    // dal `user_runs.decks_snapshot[deckKey]` al /create (vedi server.ts
    // lobby middleware) e le inietta in setupData. Game.setup le rilegge come
    // `G.players['0'].selectedCards`. Il parametro resta nella firma per il
    // sito chiamante in RunScreen (UI hint) ma qui non viene trasmesso.
    void deckCards;
    setRunMatchCtx({
      deckUsedKey: deckKey,
      onMatchEnd: (G, playerID, gameover, matchIDFromBoard) =>
        handleMatchEnd(G, playerID, gameover, targetNodeId, matchIDFromBoard),
    });
    // Difficulty del bot = difficulty del nodo target (scala con la profondità).
    const targetNode = currentRun.map?.nodes.find(n => n.id === targetNodeId);
    const botDifficulty = targetNode?.difficulty ?? 'easy';
    // Crea + join un match vs bot. Il server side risolve `runMatch.deckKey`
    // → `runMatch.deckCards` dallo snapshot della run attiva PRIMA che
    // bgio's createMatch venga eseguito (vedi server.ts /create middleware).
    try {
      const createRes = await lobbyClient.createMatch('grand-snatch', {
        numPlayers: 2,
        setupData: { botSeat: '1', botDifficulty, runMatch: { deckKey, targetNodeId } },
      }, bearerInit(auth.token));
      const joinRes = await lobbyClient.joinMatch('grand-snatch', createRes.matchID, {
        playerID: '0',
        playerName: auth.user.username,
      }, bearerInit(auth.token));
      const cred = joinRes.playerCredentials;
      localStorage.setItem(sessionKey('0'), JSON.stringify({
        matchID: createRes.matchID,
        credentials: cred,
        isBot: false,
      }));
      setMatchID(createRes.matchID);
      setCredentials(cred ?? null);
      setPlayer('0');
      setIsBot(false);
    } catch (e) {
      console.error('startMatchInRun error:', e);
      if (String((e as Error)?.message ?? e).includes('409')) {
        void dialog.alert({
          title: 'Match already in progress',
          message: 'You already have an unfinished run match. Resume it or forfeit it from the run screen (a forfeit counts as a loss).',
        });
        setRunMatchCtx({ deckUsedKey: null, onMatchEnd: null });
        void refreshRun();
        return;
      }
      void dialog.alert({
        title: 'Network error',
        message: "Couldn't create the match. Check that the server is running.",
        variant: 'error',
      });
      setRunMatchCtx({ deckUsedKey: null, onMatchEnd: null });
    }
  };

  // Hook chiamato da Board.tsx una volta a gameover. POSTa al server solo
  // gli identificativi (matchID + deckUsedKey + targetNodeId): è il server a
  // computare snatch lists / outcome / resourcesGained leggendo lo stato
  // bgio dal MySqlStore. Vedi docs/security_audit.md per il motivo.
  const handleMatchEnd = async (
    G: GrandSnatchState,
    playerID: string,
    gameover: MatchGameoverData,
    targetNodeId: string | null,
    matchIDFromBoard: string,
  ) => {
    if (!auth) return;
    // IMPORTANT: usiamo `matchIDFromBoard` invece della state-variable
    // `matchID` di AppInner. L'onMatchEnd in runMatchCtx è bindato PRIMA che
    // setMatchID sia chiamato in startMatchInRun, quindi la closure di
    // handleMatchEnd vede matchID=null. Board ce lo passa esplicitamente.
    // `G` non viene più letto qui — il server ricostruisce snatch/resources
    // direttamente dallo stato bgio. Lo lasciamo nella firma perché Board lo
    // passa, e azzerarlo qui scoraggia future regressioni "rimettiamo il
    // calcolo client per comodità". `playerID` e `gameover` restano in uso
    // sotto per decidere se chiamare advance-to-node.
    void G;
    try {
      const res = await fetch(`${SERVER_ORIGIN}/auth/runs/current/record-match`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${auth.token}` },
        // Mazzo e nodo target li legge il server dal setupData del match.
        body: JSON.stringify({ matchID: matchIDFromBoard }),
      });
      if (res.ok) {
        const j = await res.json();
        setCurrentRun(j.run as UserRunRecord);
        // Se la run è andata in stato terminale (forced exit / extracted),
        // forza tab='run' così l'utente vede la schermata di recap appena
        // torna dal match. Senza, lo sweep dell'app-reset post-gameover
        // atterra in una vista vuota (limbo).
        if (j.run && (j.run.status === 'failed' || j.run.status === 'extracted')) {
          setTab('run');
        }
        // Vincitore reale dal gameover di bgio (include location + mission bonus,
        // non solo snatchPoints). Null = pareggio → niente advance.
        const won = !!gameover && gameover.winner === playerID;
        if (won && targetNodeId && j.run?.status === 'active') {
          try {
            const r2 = await fetch(`${SERVER_ORIGIN}/auth/runs/current/advance-to-node`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${auth.token}` },
              body: JSON.stringify({ nodeId: targetNodeId }),
            });
            if (r2.ok) {
              const j2 = await r2.json();
              trackEvent('run_node_entered', { nodeId: targetNodeId, runId: j.run.id });
              setCurrentRun(j2.run as UserRunRecord);
              // Boss battuto: il server ha già chiuso la run come estrazione.
              // Stesso passaggio di un'estrazione dal Safehouse (badge "nuove
              // carte" in collezione + risorse banked), poi schermata run.
              if (j2.bossCleared) {
                trackEvent('run_extracted', { runId: j2.run.id, boss: true, cardsGainedCount: (j2.gainedCardIds ?? []).length });
                bankRunLoot(j2.resourcesGained ?? {}, j2.bankedResources ?? {}, j2.gainedCardIds ?? []);
                setTab('run');
              }
            } else {
              console.warn('advance-to-node failed', await r2.json());
            }
          } catch (e) {
            console.warn('advance-to-node network error', e);
          }
        }
      } else {
        const j = await res.json();
        console.error('record-match failed', j);
      }
    } catch (e) {
      console.error('record-match network error', e);
    }
  };

  const refreshMatches = async () => {
    if (!auth) return;
    try {
      const res = await lobbyClient.listMatches('grand-snatch', undefined, bearerInit(auth.token));
      if (res && res.matches) setMatches(res.matches);
      setServerStatus('ok');
    } catch (e) {
      setServerStatus('down');
      console.error('Error fetching matches', e);
    }
  };

  useEffect(() => {
    if (!auth) return;
    refreshMatches();
    const interval = setInterval(refreshMatches, 5000);
    return () => clearInterval(interval);
  }, [auth]);

  useEffect(() => {
    const handleLobbyExit = () => {
      if (isGuestRef.current) {
        // Guest clicked "Back to Lobby" on the gameover screen — show the
        // RegistrationGate overlay instead of tearing down the match immediately.
        // Match state is kept so the board stays mounted behind the modal.
        setGuestMatchOver(true);
        return;
      }
      setPlayer(null);
      setMatchID(null);
      setCredentials(null);
      setIsBot(false);
      
      const wasRunMatch = !!runMatchCtxRef.current.deckUsedKey;

      // Reset del context di run-match: prossimo match (se ce ne sarà) parte pulito.
      setRunMatchCtx({ deckUsedKey: null, onMatchEnd: null });
      refreshMatches();
      void refreshUserProfile();
      
      // Se il giocatore stava in una run ed era un match di run, handleMatchEnd si occupa di caricare lo stato.
      // Chiamare refreshRun qui causerebbe una race condition (GET /runs/current che sovrascrive a null lo stato failed/extracted).
      if (!wasRunMatch) {
        void refreshRun();
      }
    };
    const handleResetToCollection = () => {
      handleLobbyExit();
      setTab('collection');
    };
    window.addEventListener('app-reset', handleLobbyExit);
    window.addEventListener('app-reset-to-collection', handleResetToCollection);
    return () => {
      window.removeEventListener('app-reset', handleLobbyExit);
      window.removeEventListener('app-reset-to-collection', handleResetToCollection);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth]);

  // Per-user session key so two accounts on the same browser don't collide.
  const sessionKey = (playerIdStr: string) =>
    auth ? `gs_session_${auth.user.id}_p${playerIdStr}` : '';

  // For each in-progress match in the lobby list, figure out if the current
  // user can resume it: their username must occupy a seat AND we must still
  // have the session credentials stored locally (needed by bgio to dispatch
  // moves). Recomputed on `matches` refresh (every 5s) — fresh enough.
  const resumeMap = useMemo(() => {
    const map = new Map<string, { seat: string; creds: string }>();
    if (!auth) return map;
    for (const m of matches) {
      if (m.gameover) continue;
      for (let i = 0; i < (m.players?.length ?? 0); i++) {
        if (m.players[i]?.name !== auth.user.username) continue;
        const saved = localStorage.getItem(sessionKey(String(i)));
        if (!saved) continue;
        try {
          const parsed = JSON.parse(saved);
          if (parsed.matchID === m.matchID && parsed.credentials) {
            map.set(m.matchID, { seat: String(i), creds: parsed.credentials });
            break;
          }
        } catch { /* malformed session entry — skip */ }
      }
    }
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matches, auth]);

  const resumeMatch = (matchID: string) => {
    // Partita della run: riprende con il contesto della run (registrazione
    // e avanzamento a fine partita), non come partita amichevole.
    if (currentRun?.activeMatch?.matchID === matchID) { resumeRunMatch(matchID); return; }
    const info = resumeMap.get(matchID);
    if (!info) return;
    setMatchID(matchID);
    setCredentials(info.creds);
    setPlayer(info.seat);
    setIsBot(false);
  };

  // Riprende la partita della run in corso usando le credenziali salvate su
  // questo browser. false = non riprendibile da qui (altro device).
  const resumeRunMatch = (matchID: string): boolean => {
    const am = currentRun?.activeMatch;
    if (!auth || !am || am.matchID !== matchID) return false;
    let creds: string | null = null;
    try {
      const saved = JSON.parse(localStorage.getItem(sessionKey('0')) ?? 'null');
      if (saved?.matchID === matchID) creds = saved.credentials ?? null;
    } catch { /* ignore */ }
    if (!creds) return false;
    setRunMatchCtx({
      deckUsedKey: am.deckKey,
      onMatchEnd: (G, playerID, gameover, matchIDFromBoard) =>
        handleMatchEnd(G, playerID, gameover, am.targetNodeId, matchIDFromBoard),
    });
    setMatchID(matchID);
    setCredentials(creds);
    setPlayer('0');
    setIsBot(false);
    return true;
  };

  // Friendly PvP matchmaking ("Play vs Player"). Reconnect to a still-live
  // saved session if any, else join an open friendly match (a non-bot match
  // with a free seat), else create a fresh one and take seat 0. No stakes —
  // the risk/reward loop is Run mode.
  const playVsPlayer = async (deckId: string | null) => {
    if (!auth) return;
    try {
      const res = await lobbyClient.listMatches('grand-snatch', undefined, bearerInit(auth.token));

      // Reconnect: una sessione salvata ancora viva, ma SOLO se è un match PvP
      // amichevole. I match vs bot e quelli di run hanno setupData.botSeat e non
      // si riprendono da qui (playVsBot e il run creano sempre un match nuovo).
      for (const seat of ['0', '1']) {
        const savedStr = localStorage.getItem(sessionKey(seat));
        if (!savedStr) continue;
        const saved = JSON.parse(savedStr);
        const live = res.matches.find(m => m.matchID === saved.matchID && !m.gameover);
        if (live && !(live.setupData as { botSeat?: string } | undefined)?.botSeat) {
          setMatchID(saved.matchID);
          setCredentials(saved.credentials);
          setPlayer(seat);
          setIsBot(false);
          return;
        }
        localStorage.removeItem(sessionKey(seat));
      }

      // Cerca una partita amichevole con un posto libero. Esclude i match vs
      // bot: hanno setupData.botSeat e quel posto è riservato al bot driver.
      const open = res.matches.find(m =>
        !m.gameover &&
        !(m.setupData as { botSeat?: string } | undefined)?.botSeat &&
        m.players.some(p => !p.name)
      );

      let targetMatchId: string;
      let seat: string;
      let creds: string | undefined;

      if (open) {
        seat = String(open.players.find(p => !p.name)!.id);
        targetMatchId = open.matchID;
        const joinRes = await lobbyClient.joinMatch('grand-snatch', targetMatchId, {
          playerID: seat,
          playerName: auth.user.username,
        }, bearerInit(auth.token));
        creds = joinRes.playerCredentials;
      } else {
        const createRes = await lobbyClient.createMatch('grand-snatch', { numPlayers: 2 }, bearerInit(auth.token));
        targetMatchId = createRes.matchID;
        seat = '0';
        const joinRes = await lobbyClient.joinMatch('grand-snatch', targetMatchId, {
          playerID: seat,
          playerName: auth.user.username,
        }, bearerInit(auth.token));
        creds = joinRes.playerCredentials;
      }

      // Pre-commit the deck if selected
      if (deckId) {
        const commitRes = await fetch(`${SERVER_ORIGIN}/auth/matches/${targetMatchId}/commit-deck`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${auth.token}` },
          body: JSON.stringify({ deckId }),
        });
        if (!commitRes.ok) {
          const j = await commitRes.json().catch(() => ({}));
          throw new Error(j.error ?? 'Failed to commit selected deck');
        }
      }

      localStorage.setItem(sessionKey(seat), JSON.stringify({
        matchID: targetMatchId,
        credentials: creds,
        isBot: false,
      }));
      setMatchID(targetMatchId);
      setCredentials(creds ?? null);
      setPlayer(seat);
      setIsBot(false);
    } catch (e) {
      console.error('playVsPlayer error:', e);
      void dialog.alert({
        title: 'Matchmaking error',
        message: e instanceof Error ? e.message : 'Connection error to the Lobby. Check that the server is running.',
        variant: 'error',
      });
    }
  };

  // Spin up a fresh match flagged for the server-side bot to fill seat 1,
  // then join seat 0 ourselves. The BotManager (server-side) will pick up
  // the flag on its next poll and attach an in-process bot client. Single
  // tab, no race conditions from cross-tab moves.
  const playVsBot = async (difficulty: BotDifficulty, deckId: string | null) => {
    if (!auth) return;
    try {
      const createRes = await lobbyClient.createMatch('grand-snatch', {
        numPlayers: 2,
        setupData: { botSeat: '1', botDifficulty: difficulty },
      }, bearerInit(auth.token));
      const joinRes = await lobbyClient.joinMatch('grand-snatch', createRes.matchID, {
        playerID: '0',
        playerName: auth.user.username,
      }, bearerInit(auth.token));
      const credentials = joinRes.playerCredentials;

      // Pre-commit the deck if selected
      if (deckId) {
        const commitRes = await fetch(`${SERVER_ORIGIN}/auth/matches/${createRes.matchID}/commit-deck`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${auth.token}` },
          body: JSON.stringify({ deckId }),
        });
        if (!commitRes.ok) {
          const j = await commitRes.json().catch(() => ({}));
          throw new Error(j.error ?? 'Failed to commit selected deck');
        }
      }

      localStorage.setItem(sessionKey('0'), JSON.stringify({
        matchID: createRes.matchID,
        credentials,
        isBot: false,
      }));
      setMatchID(createRes.matchID);
      setCredentials(credentials ?? null);
      setPlayer('0');
      setIsBot(false);
    } catch (e) {
      console.error('playVsBot error:', e);
      void dialog.alert({
        title: 'Match creation error',
        message: e instanceof Error ? e.message : "Couldn't create the vs Bot match. Check that the server is running.",
        variant: 'error',
      });
    }
  };

  const deleteMatch = async (id: string) => {
    const m = matches.find(x => x.matchID === id);
    const isRunMatch = !!(m?.setupData as { runMatch?: unknown } | undefined)?.runMatch && !m?.gameover;
    const ok = await dialog.confirm({
      title: isRunMatch ? 'Forfeit this run match?' : 'Delete match?',
      message: isRunMatch
        ? 'This match belongs to your run. Deleting it means forfeiting it, and it counts as a LOSS: cards the opponent snatched are lost, and if they snatched none, one random card of the deck you used is sacrificed.'
        : `Are you sure you want to delete match ${id.substring(0, 6)}…?\nThis action cannot be undone.`,
      okLabel: isRunMatch ? 'Forfeit (count as loss)' : 'Delete',
      cancelLabel: 'Cancel',
      variant: 'danger',
    });
    if (!ok) return;
    try {
      const response = await fetch(`${serverOrigin}/custom/matches/${id}`, {
        method: 'DELETE',
        headers: auth ? { Authorization: `Bearer ${auth.token}` } : undefined,
      });
      if (response.ok) { refreshMatches(); void refreshRun(); }
      else void dialog.alert({ message: 'Error while deleting the match.', variant: 'error' });
    } catch (e) {
      console.error('Delete error:', e);
      void dialog.alert({ message: 'Network error during deletion.', variant: 'error' });
    }
  };

  // 1. Not authenticated and no guest match in progress → Login screen
  if (!auth && !isGuest) {
    return <Login onAuth={handleAuth} onGuestPlay={playAsGuest} />;
  }

  // Snapshot of the user's current location for feedback reports — passed
  // through to the FeedbackWidget so a bug report carries the screen the
  // user was on, plus the active run/match id if any.
  const feedbackContextSnapshot = () => ({
    tab: player && matchID ? 'match' : tab,
    runId: currentRun?.id,
    matchId: matchID ?? undefined,
  });

  // 2. In a match → render the game board (also covers guest tutorial match)
  if (player && matchID) {
    return (
      <TutorialContext.Provider value={{ active: isGuest }}>
        <BotContext.Provider value={isBot}>
          <RunMatchContext.Provider value={runMatchCtx}>
            <ErrorBoundary>
              <GameClient matchID={matchID} playerID={player} credentials={credentials || undefined} />
            </ErrorBoundary>
            {(auth || guestToken) && <FeedbackWidget token={auth?.token || guestToken || ''} contextSnapshot={feedbackContextSnapshot} />}
            {guestMatchOver && (
              <RegistrationGate
                onAuth={handleGuestAuth}
                onMaybeLater={handleGuestMaybeLater}
                tutorialClaim={tutorialClaim}
              />
            )}
          </RunMatchContext.Provider>
        </BotContext.Provider>
      </TutorialContext.Provider>
    );
  }

  // 3. Safety net: no auth and no active match (edge case: guest teardown race)
  if (!auth) {
    return <Login onAuth={handleAuth} onGuestPlay={playAsGuest} />;
  }

  // 4. Authenticated, not in match → app shell with tabs
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', background: 'var(--bg-color)', color: 'var(--text-color)' }}>
      {auth.user.email && !auth.user.emailVerified && (
        <UnverifiedEmailBanner
          email={auth.user.email}
          token={auth.token}
          onMarkVerified={() => updateAuthUser({ emailVerified: true })}
        />
      )}
      {/* Top bar */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '8px 16px', borderBottom: '2px solid var(--accent-color)', background: '#111',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
          <div style={{ color: 'var(--accent-color)', fontSize: '20px', fontWeight: 'bold' }}>GrandSnatch</div>
          {currentRun?.status === 'active' ? (
            <button
              onClick={() => setTab('run')}
              style={tabBtn(tab === 'run')}
            >Run <span style={{ fontSize: 10, marginLeft: 4 }}>●</span></button>
          ) : (
            <button
              onClick={() => setTab('lobby')}
              style={tabBtn(tab === 'lobby')}
            >Lobby</button>
          )}
          <button
            onClick={() => setTab('collection')}
            style={{ ...tabBtn(tab === 'collection'), position: 'relative' }}
          >
            Your Cards
            {hasNewLoot && (
              <span style={{
                position: 'absolute',
                top: '4px',
                right: '4px',
                width: '8px',
                height: '8px',
                backgroundColor: '#ff1744',
                borderRadius: '50%',
                boxShadow: '0 0 8px #ff1744',
                animation: 'pulse-dot 1.5s infinite alternate'
              }} />
            )}
          </button>
          <button
            onClick={() => setTab('account')}
            style={tabBtn(tab === 'account')}
          >Account</button>
          {auth.user.isAdmin && (
            <button
              onClick={() => setTab('admin')}
              style={tabBtn(tab === 'admin')}
            >Admin Console</button>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <ServerStatusBadge status={serverStatus} />
          <span style={{ color: '#bbb', fontSize: '13px' }}>Signed in as <strong>{auth.user.username}</strong></span>
          <button onClick={logout} style={{ ...tabBtn(false), color: '#e57373' }}>Logout</button>
        </div>
      </div>

      {/* Content area */}
      <div style={{ flex: 1, overflow: 'hidden' }}>
        {tab === 'lobby' && (!currentRun || currentRun.status !== 'active') && (
          <LobbyView
            matches={matches}
            resumableMatchIds={new Set(resumeMap.keys())}
            onResume={resumeMatch}
            onPlayVsPlayer={() => setDeckSelectorMode('pvp')}
            onPlayVsBot={() => setDeckSelectorMode('bot')}
            onDelete={deleteMatch}
            serverDown={serverStatus === 'down'}
            onStartRun={() => setRunStarterOpen(true)}
          />
        )}
        {tab === 'run' && currentRun && (
          <RunScreen
            run={currentRun}
            token={auth.token}
            user={auth.user}
            onUpdateSettings={updateUserSettings}
            onAbandoned={abandonOrFailRun}
            onStartMatch={startMatchInRun}
            onRefresh={(r) => setCurrentRun(prev => {
              if (!r && prev && (prev.status === 'failed' || prev.status === 'extracted')) {
                return prev;
              }
              return r;
            })}
            onResourcesBanked={bankRunLoot}
            onResumeMatch={resumeRunMatch}
          />
        )}
        {tab === 'collection' && (
          <Collection
            token={auth.token}
            currentAvatarId={auth.user.avatarId}
            onAvatarChange={(id) => updateAuthUser({ avatarId: id })}
            bankedResources={auth.user.bankedResources ?? {}}
            stats={auth.user.stats}
          />
        )}
        {tab === 'account' && (
          <AccountSettings
            token={auth.token}
            user={auth.user}
            onUpdateSettings={updateUserSettings}
            onAccountDeleted={logout}
          />
        )}
        {tab === 'admin' && auth.user.isAdmin && (
          <AdminDashboard
            token={auth.token}
            onGoBack={() => setTab('lobby')}
          />
        )}
      </div>

      {/* In the Account tab the Legal section inside the page already exposes
          these links — the footer would be redundant noise. */}
      {tab !== 'account' && <LegalFooter />}

      <FeedbackWidget token={auth.token} contextSnapshot={feedbackContextSnapshot} />

      {runStarterOpen && (
        <RunStarter
          token={auth.token}
          onCancel={() => setRunStarterOpen(false)}
          onStarted={(run) => {
            setRunStarterOpen(false);
            setCurrentRun(run);
            setTab('run');
          }}
        />
      )}

      {deckSelectorMode && auth && (
        <DeckSelectionModal
          token={auth.token}
          onPick={(deckId) => {
            if (deckSelectorMode === 'bot') {
              setSelectedDeckIdForAI(deckId);
              setDeckSelectorMode(null);
              setBotPickerOpen(true);
            } else {
              setDeckSelectorMode(null);
              void playVsPlayer(deckId);
            }
          }}
          onCancel={() => setDeckSelectorMode(null)}
          onGoToCollection={() => {
            setDeckSelectorMode(null);
            setTab('collection');
          }}
        />
      )}

      {botPickerOpen && (
        <BotDifficultyModal
          onCancel={() => {
            setBotPickerOpen(false);
            setSelectedDeckIdForAI(undefined);
          }}
          onPick={(d) => {
            setBotPickerOpen(false);
            const deckId = selectedDeckIdForAI;
            setSelectedDeckIdForAI(undefined);
            void playVsBot(d, deckId ?? null);
          }}
        />
      )}
    </div>
  );
}

// Modal to pick the AI strength before a friendly vs-bot match.
function BotDifficultyModal({
  onPick, onCancel,
}: {
  onPick: (d: BotDifficulty) => void;
  onCancel: () => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onCancel(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  const options: { d: BotDifficulty; label: string; desc: string; color: string }[] = [
    { d: 'easy',   label: 'Easy',   desc: 'Plays at random — good for learning the ropes.',          color: '#4CAF50' },
    { d: 'medium', label: 'Medium', desc: 'Greedy — grabs the best loot every turn.',                color: '#ffb703' },
    { d: 'hard',   label: 'Hard',   desc: 'Sharp — looks a move ahead and plays the objectives.',    color: '#e57373' },
  ];

  return (
    <div
      onClick={onCancel}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#0c0c16', border: '2px solid var(--accent-color)',
          borderRadius: 10, padding: 24, width: 380, maxWidth: '92vw',
          color: 'var(--text-color)',
        }}
      >
        <h2 style={{ margin: '0 0 4px', color: 'var(--accent-color)', fontSize: 18 }}>Choose AI difficulty</h2>
        <p style={{ color: '#888', fontSize: 12, margin: '0 0 16px' }}>Friendly match — no stakes.</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {options.map(o => (
            <button
              key={o.d}
              onClick={() => onPick(o.d)}
              style={{
                textAlign: 'left', padding: '10px 14px', background: '#15151f',
                border: `2px solid ${o.color}`, borderRadius: 6, cursor: 'pointer',
                fontFamily: 'inherit', color: '#fff',
              }}
            >
              <div style={{ fontWeight: 'bold', color: o.color, fontSize: 14 }}>{o.label}</div>
              <div style={{ fontSize: 11, color: '#aaa', marginTop: 2 }}>{o.desc}</div>
            </button>
          ))}
        </div>
        <button
          onClick={onCancel}
          style={{
            marginTop: 14, background: 'none', border: '1px solid #555', color: '#ccc',
            padding: '6px 14px', borderRadius: 4, cursor: 'pointer', fontFamily: 'inherit', fontSize: 12,
          }}
        >
          Cancel (Esc)
        </button>
      </div>
    </div>
  );
}

function ServerStatusBadge({ status }: { status: ServerStatus }) {
  const config = status === 'ok'
    ? { dot: '#4CAF50', text: 'Server Online', color: '#4CAF50' }
    : status === 'down'
      ? { dot: '#e57373', text: 'Server Offline', color: '#e57373' }
      : { dot: '#888', text: 'Checking...', color: '#888' };
  return (
    <span
      title={status === 'down' ? 'The server is not responding. Try again in a few seconds or check it is running (npm run server).' : ''}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: '6px',
        fontSize: '12px', color: config.color,
        padding: '4px 10px', borderRadius: '4px',
        border: `1px solid ${config.color}`,
        animation: status === 'down' ? 'pulse 1.5s infinite' : undefined,
      }}
    >
      <span style={{
        width: '8px', height: '8px', borderRadius: '50%', background: config.dot,
      }} />
      {config.text}
    </span>
  );
}

// Slim banner shown while the account's email is still pending verification.
// Lets the user fire a resend or dismiss for the session (the dismiss is just
// cosmetic — the gate re-appears at next reload until the address is verified).
function UnverifiedEmailBanner({
  email, token, onMarkVerified,
}: {
  email: string;
  token: string;
  onMarkVerified: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState<'idle' | 'ok' | 'err'>('idle');
  const [errMsg, setErrMsg] = useState<string>('');

  // Re-sync on mount: the user might have clicked the verify link in another
  // tab. A single /auth/me poll catches that without forcing a logout.
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${SERVER_ORIGIN}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const j = await res.json();
        if (j.emailVerified) onMarkVerified();
      } catch { /* offline — banner stays */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resend = async () => {
    setBusy(true);
    setErrMsg('');
    try {
      const res = await fetch(`${SERVER_ORIGIN}/auth/resend-verification`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) { setSent('ok'); }
      else {
        const j = await res.json().catch(() => ({}));
        setSent('err');
        setErrMsg(j.error ?? 'Resend failed');
      }
    } catch {
      setSent('err');
      setErrMsg('Network error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{
      background: '#3a2a08', borderBottom: '1px solid #ffb703', color: '#ffe9b5',
      fontSize: 12, padding: '8px 16px', display: 'flex', alignItems: 'center',
      gap: 12, justifyContent: 'center', flexWrap: 'wrap',
    }}>
      <span>📬 Confirm your email <strong>{email}</strong> — some features stay locked until verified.</span>
      {sent === 'ok' ? (
        <span style={{ color: '#4CAF50' }}>Sent. Check your inbox (and spam).</span>
      ) : (
        <button
          onClick={resend}
          disabled={busy}
          style={{
            background: '#ffb703', color: '#000', border: 'none',
            padding: '3px 10px', borderRadius: 3, fontSize: 11, fontWeight: 'bold',
            cursor: busy ? 'wait' : 'pointer', fontFamily: 'inherit',
          }}
        >{busy ? 'Sending...' : 'Resend verification'}</button>
      )}
      {sent === 'err' && <span style={{ color: '#e57373' }}>{errMsg}</span>}
    </div>
  );
}

// Slim footer with links to the legal docs. Same-tab navigation: clicking
// pushes a history entry, then the "← Back" button on LegalPage steps you
// back into the app with state restored by the browser's bfcache. Opening
// in a new tab (middle-click / ctrl-click) is still available to power users.
function LegalFooter() {
  return (
    <div style={{
      borderTop: '1px solid #1f1f2a', background: '#0a0a12', color: '#666',
      fontSize: 10, padding: '6px 16px', display: 'flex', justifyContent: 'center',
      gap: 16, flexWrap: 'wrap', flexShrink: 0,
    }}>
      <a href="/legal/privacy" style={legalLinkStyle}>Privacy Policy</a>
      <span>·</span>
      <a href="/legal/terms"   style={legalLinkStyle}>Terms of Service</a>
      <span>·</span>
      <a href="/legal/cookies" style={legalLinkStyle}>Cookie Policy</a>
    </div>
  );
}

const legalLinkStyle: React.CSSProperties = {
  color: '#888', textDecoration: 'none',
};

function tabBtn(active: boolean): React.CSSProperties {
  return {
    background: active ? 'var(--accent-color)' : 'transparent',
    color: active ? '#000' : '#ccc',
    border: '1px solid #444',
    padding: '6px 14px',
    borderRadius: '4px',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: '13px',
  };
}

function LobbyView({
  matches, resumableMatchIds, onResume, onPlayVsPlayer, onPlayVsBot, onDelete, serverDown, onStartRun,
}: {
  matches: any[];
  resumableMatchIds: Set<string>;
  onResume: (matchID: string) => void;
  onPlayVsPlayer: () => void;
  onPlayVsBot: () => void;
  onDelete: (id: string) => void;
  serverDown: boolean;
  onStartRun: () => void;
}) {
  return (
    <div style={{ display: 'flex', height: '100%' }}>
      <div style={{ width: '300px', background: '#111', padding: '20px', borderRight: '2px solid var(--accent-color)', overflowY: 'auto' }}>
        <h3 style={{ fontSize: '14px', marginBottom: '10px' }}>Matches</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {matches.length === 0 ? (
            <p style={{ fontSize: '12px', color: '#888' }}>No matches found...</p>
          ) : (
            matches.map(m => (
              <div key={m.matchID} className="lobby-match-card" style={{ borderLeft: m.gameover ? '3px solid #4CAF50' : '3px solid #ffb703' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>Match: {m.matchID.substring(0, 6)}...</div>
                  <button
                    className="delete-match-btn"
                    onClick={(e) => { e.stopPropagation(); onDelete(m.matchID); }}
                    title="Delete match"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /><line x1="10" y1="11" x2="10" y2="17" /><line x1="14" y1="11" x2="14" y2="17" /></svg>
                  </button>
                </div>
                <div style={{ color: '#aaa', marginBottom: '5px' }}>
                  {m.players[0]?.name || '[Empty]'} vs {m.players[1]?.name || '[Empty]'}
                </div>
                {m.gameover ? (
                  <div style={{ color: '#4CAF50' }}>
                    {m.gameover.interrupted ? 'Interrupted' : (m.gameover.winner ? `Winner: P${m.gameover.winner}` : 'Draw')}
                  </div>
                ) : (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                    <span style={{ color: '#ffb703' }}>In progress...</span>
                    {resumableMatchIds.has(m.matchID) && (
                      <button
                        onClick={(e) => { e.stopPropagation(); onResume(m.matchID); }}
                        title="Rejoin this match"
                        style={{
                          background: 'var(--accent-color)', color: '#000', border: 'none',
                          padding: '3px 10px', borderRadius: 3, fontSize: 11, fontWeight: 'bold',
                          cursor: 'pointer', fontFamily: 'inherit',
                        }}
                      >▶ Resume</button>
                    )}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24, overflowY: 'auto' }}>
        <h2 style={{ marginBottom: '0.4rem' }}>Choose a mode</h2>
        <p style={{ color: '#888', fontSize: 12, marginTop: 0, marginBottom: 24 }}>
          Two friendly modes with no stakes, plus the Run extraction loop.
        </p>
        {serverDown && (
          <div style={{
            color: '#e57373', fontSize: '13px', marginBottom: '16px',
            background: '#220', border: '1px solid #e57373',
            padding: '8px 16px', borderRadius: '4px',
          }}>
            ⚠ The server isn't responding. Lobby actions are disabled.
          </div>
        )}
        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', justifyContent: 'center' }}>
          <ModeCard
            emoji="🤝"
            title="Play vs Player"
            tag="Friendly · no stakes"
            tagColor="#4fc3f7"
            accent="#4fc3f7"
            desc="Quick match against another player in the lobby. Nothing is won or lost — just play."
            disabled={serverDown}
            onClick={onPlayVsPlayer}
          />
          <ModeCard
            emoji="🤖"
            title="Play vs AI"
            tag="Friendly · no stakes"
            tagColor="#4fc3f7"
            accent="#e57373"
            desc="Match a bot at your chosen difficulty — easy, medium or hard. Practice with zero risk."
            disabled={serverDown}
            onClick={onPlayVsBot}
          />
          <ModeCard
            emoji="🎴"
            title="Run"
            tag="Risk & reward"
            tagColor="#ffcc00"
            accent="var(--accent-color)"
            desc="The extraction loop. Pick 2 decks and cross a map of matches — opponents' snatches PHYSICALLY take cards from your decks. Extract to keep your loot, or lose it all."
            disabled={serverDown}
            onClick={onStartRun}
          />
        </div>
      </div>
    </div>
  );
}

// One of the three homepage mode entries.
function ModeCard({
  emoji, title, tag, tagColor, accent, desc, disabled, onClick,
}: {
  emoji: string;
  title: string;
  tag: string;
  tagColor: string;
  accent: string;
  desc: string;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      disabled={disabled}
      onClick={onClick}
      style={{
        width: 260, minHeight: 290,
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        textAlign: 'center', gap: 10,
        padding: '28px 22px',
        background: '#0c0c16',
        border: `2px solid ${accent}`,
        borderRadius: 10,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.4 : 1,
        fontFamily: 'inherit', color: '#fff',
        boxShadow: `0 0 14px ${accent}33`,
        transition: 'transform 0.12s ease',
      }}
      onMouseEnter={(e) => { if (!disabled) e.currentTarget.style.transform = 'translateY(-4px)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
    >
      <div style={{ fontSize: 46, lineHeight: 1 }}>{emoji}</div>
      <div style={{ fontSize: 20, fontWeight: 'bold', color: accent }}>{title}</div>
      <div style={{
        fontSize: 10, fontWeight: 'bold', letterSpacing: 0.5,
        color: '#000', background: tagColor,
        padding: '2px 8px', borderRadius: 3,
      }}>{tag}</div>
      <p style={{ fontSize: 12, color: '#aaa', margin: 0, lineHeight: 1.45 }}>{desc}</p>
    </button>
  );
}

interface UserDeckRecord {
  id: string;
  name: string;
  createdAt: string;
  cards: { id: string; card: CardData; acquiredAt: string; source: string }[];
}

function DeckSelectionModal({
  token,
  onPick,
  onCancel,
  onGoToCollection,
}: {
  token: string;
  onPick: (deckId: string | null) => void;
  onCancel: () => void;
  onGoToCollection: () => void;
}) {
  const [decks, setDecks] = useState<UserDeckRecord[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await fetch(`${SERVER_ORIGIN}/auth/decks`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (!active) return;
        if (!res.ok) {
          setError(data.error ?? 'Failed to load decks');
        } else {
          setDecks(data.decks as UserDeckRecord[]);
        }
      } catch (e) {
        if (active) setError(e instanceof Error ? e.message : 'Network error');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [token]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onCancel(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  return (
    <div
      onClick={onCancel}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#0c0c16', border: '2px solid var(--accent-color)',
          borderRadius: 12, padding: 24, width: 520, maxWidth: '92vw',
          maxHeight: '85vh', display: 'flex', flexDirection: 'column',
          color: 'var(--text-color)', boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
        }}
      >
        <h2 style={{ margin: '0 0 4px', color: 'var(--accent-color)', fontSize: 18 }}>Select your deck</h2>
        <p style={{ color: '#888', fontSize: 12, margin: '0 0 16px' }}>Choose a complete deck to play, or pick a starter deck.</p>

        <div style={{ flex: 1, overflowY: 'auto', marginBottom: 20, paddingRight: 4 }}>
          {loading && <div style={{ color: '#aaa', fontSize: 13, textAlign: 'center', margin: '20px 0' }}>Loading decks...</div>}
          {error && <div style={{ color: '#e57373', fontSize: 13, textAlign: 'center', margin: '20px 0' }}>Error: {error}</div>}
          
          {!loading && !error && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {/* Starter Deck Option */}
              <button
                onClick={() => onPick(null)}
                style={{
                  textAlign: 'left', padding: '14px 18px', background: '#1d1d2b',
                  border: '2px solid #555', borderRadius: 8, cursor: 'pointer',
                  fontFamily: 'inherit', color: '#fff', transition: 'all 0.2s',
                }}
                onMouseOver={(e) => (e.currentTarget.style.borderColor = 'var(--accent-color)')}
                onMouseOut={(e) => (e.currentTarget.style.borderColor = '#555')}
              >
                <div style={{ fontWeight: 'bold', color: '#ffb703', fontSize: 14 }}>✨ Starter Deck (Suit Draft)</div>
                <div style={{ fontSize: 11, color: '#aaa', marginTop: 4 }}>
                  No custom deck needed. Draft 2 random suits procedurally when the match starts.
                </div>
              </button>

              {/* User Decks */}
              {decks && decks.length > 0 ? (
                decks.map(deck => {
                  const cards = deck.cards.map(r => r.card);
                  const suitsInDeck = Array.from(new Set(cards.map(c => c.suit)));
                  const validation = validateDeckComposition(cards);
                  const isValid = validation.valid;
                  const reason = !isValid ? validation.reason : '';

                  return (
                    <button
                      key={deck.id}
                      disabled={!isValid}
                      onClick={() => onPick(deck.id)}
                      style={{
                        textAlign: 'left', padding: '14px 18px',
                        background: isValid ? '#14141f' : '#0e0e14',
                        border: `2px solid ${isValid ? '#3f51b5' : '#222'}`,
                        borderRadius: 8, cursor: isValid ? 'pointer' : 'not-allowed',
                        fontFamily: 'inherit', color: '#fff', opacity: isValid ? 1 : 0.45,
                        transition: 'all 0.2s',
                      }}
                      onMouseOver={(e) => {
                        if (isValid) e.currentTarget.style.borderColor = 'var(--accent-color)';
                      }}
                      onMouseOut={(e) => {
                        if (isValid) e.currentTarget.style.borderColor = '#3f51b5';
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontWeight: 'bold', color: isValid ? '#fff' : '#888', fontSize: 14 }}>{deck.name}</span>
                        <span style={{ fontSize: 11, color: '#888' }}>{cards.length} cards</span>
                      </div>
                      <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
                        {suitsInDeck.map(s => (
                          <span key={s} style={{ background: '#222', color: '#ccc', fontSize: 9, padding: '2px 6px', borderRadius: 3 }}>
                            {s}
                          </span>
                        ))}
                      </div>
                      {!isValid && (
                        <div style={{ color: '#e57373', fontSize: 10, marginTop: 6 }}>
                          ⚠ {reason}
                        </div>
                      )}
                    </button>
                  );
                })
              ) : (
                <div style={{ color: '#888', fontSize: 12, textAlign: 'center', padding: '10px 0' }}>
                  No custom decks found.
                </div>
              )}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid #222', paddingTop: 14 }}>
          <button
            onClick={onGoToCollection}
            style={{
              background: 'none', border: 'none', color: 'var(--accent-color)',
              textDecoration: 'underline', cursor: 'pointer', fontFamily: 'inherit', fontSize: 12,
              padding: 0,
            }}
          >
            Go to Deck Builder
          </button>
          
          <button
            onClick={onCancel}
            style={{
              background: 'none', border: '1px solid #555', color: '#ccc',
              padding: '6px 14px', borderRadius: 4, cursor: 'pointer', fontFamily: 'inherit', fontSize: 12,
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
